/**
 * SessionDetailModal
 *
 * Props:
 *   sessionId  – se informado, busca detalhe completo da sessão (sets/reps/peso)
 *   checkin    – objeto de fallback (snapshot_plan_items, date, duration, etc.)
 *               usado quando não há sessionId (checkins antigos)
 *   onClose    – () => void
 */
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faDumbbell, faStar, faClipboardList, faPenToSquare, faPersonRunning } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./SessionDetailModal.module.css";
import ModalHeader from "./ModalHeader";
import SessionSummaryButton from "./SessionSummaryButton";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

function fmtDateTime(iso) {
  if (!iso) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(iso));
}

function fmtTime(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", timeStyle: "short" }).format(new Date(iso));
}

// Ordem de execução x prescrição: só chega pro professor/admin
// (order_analysis vem do backend só pra quem consulta a sessão de um aluno).
// O aluno nunca recebe o campo, então nada disto aparece pra ele.
const ORDER_LEVEL_TEXT = {
  na_ordem: "Ordem igual à prescrita",
  pequena_troca: "Ordem: pequena troca em relação à prescrita",
  reordenada: "Ordem diferente da prescrita",
};

function OrderBar({ analysis, view, onView }) {
  if (!analysis) return null;
  if (!analysis.available) {
    if (analysis.reason !== "sem_ordem_registrada") return null;
    return <p className={styles.orderLine}>Ordem de execução não registrada neste treino</p>;
  }
  const differs = analysis.level !== "na_ordem";
  return (
    <div className={styles.orderBar}>
      <div className={styles.orderTop}>
        <p className={styles.orderLine}>
          {ORDER_LEVEL_TEXT[analysis.level]}
          {analysis.started_with_other ? " · começou por outro exercício" : ""}
        </p>
        {differs && (
          <div className={styles.orderToggle} role="group" aria-label="Ordem dos exercícios">
            <button type="button" aria-pressed={view === "prescrita"} onClick={() => onView("prescrita")}>
              Ordem prescrita
            </button>
            <button type="button" aria-pressed={view === "feita"} onClick={() => onView("feita")}>
              Ordem feita
            </button>
          </div>
        )}
      </div>
      {differs && analysis.audience !== "self" && (
        <p className={styles.orderNote}>A ordem pode refletir aparelho ocupado. O aluno não informa o motivo.</p>
      )}
      {analysis.excluded > 0 && (
        <p className={styles.orderNote}>
          {analysis.excluded === 1 ? "1 exercício concluído" : `${analysis.excluded} exercícios concluídos`} ao finalizar o
          treino {analysis.excluded === 1 ? "ficou fora" : "ficaram fora"} da comparação.
        </p>
      )}
    </div>
  );
}

function SetRow({ s, index, editing, onChange }) {
  if (editing) {
    return (
      <div className={styles.setRow}>
        <span className={styles.setNum}>{index + 1}</span>
        <input
          className={styles.setEditInput}
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          min="0"
          value={s.reps ?? ""}
          onChange={e => onChange(index, "reps", e.target.value === "" ? null : parseInt(e.target.value))}
        />
        <input
          className={styles.setEditInput}
          type="number"
          inputMode="decimal"
          pattern="[0-9]*"
          min="0"
          step="0.5"
          value={s.weight ?? ""}
          onChange={e => onChange(index, "weight", e.target.value === "" ? null : parseFloat(e.target.value))}
        />
        <span className={styles.setDone}><FontAwesomeIcon icon={faCheck} /></span>
      </div>
    );
  }
  return (
    <div className={styles.setRow}>
      <span className={styles.setNum}>{index + 1}</span>
      <span className={styles.setVal}>{s.reps ?? "-"}</span>
      <span className={styles.setVal}>{s.weight != null ? `${s.weight} kg` : "-"}</span>
      <span className={styles.setDone}><FontAwesomeIcon icon={faCheck} /></span>
    </div>
  );
}

function ExerciseCard({ item, sessionId, onSaved, order, level, view, audience, finishedAt }) {
  // pro aluno o desvio aparece em cinza (informativo); o âmbar é do professor
  const movedStyle = audience === "self" ? styles.orderChip : styles.orderChipMoved;
  const rawSets = Array.isArray(item.sets_log)
    ? item.sets_log.filter(s => s.reps != null || s.weight != null)
    : [];

  // Fechado pelo sistema ao finalizar/abandonar: horário igual ao fim da sessão e
  // nenhuma série real (vazio, ou só zeros marcados auto_filled). O aluno não fez.
  const closedAtEnd = !!item.completed_at && !!finishedAt
    && new Date(item.completed_at).getTime() === new Date(finishedAt).getTime();
  const noRealSets = !Array.isArray(item.sets_log) || item.sets_log.every(s => s.auto_filled === true);
  const notDone = closedAtEnd && noRealSets;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rawSets);
  const [saving, setSaving] = useState(false);

  const handleChange = (index, field, value) => {
    setDraft(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/workout-sessions/${sessionId}/items/${item.id}/sets-log`, {
        sets_log: draft,
      });
      setEditing(false);
      onSaved(item.id, draft);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const sets = notDone ? [] : (editing ? draft : rawSets);

  return (
    <div className={styles.exerciseCard}>
      <div className={styles.exerciseHeader}>
        <div className={styles.exerciseInfo}>
          <span className={styles.exerciseName}>{item.exercise_name || item.name || "Exercício"}</span>
          {item.muscle_group && (
            <span className={styles.exerciseMuscle}>{item.muscle_group}</span>
          )}
          {order && order.executed_rank !== order.prescribed_rank && view === "prescrita" && (
            <span className={order.moved && level === "reordenada" ? movedStyle : styles.orderChip}>
              feito em {order.executed_rank}º · prescrito {order.prescribed_rank}º
            </span>
          )}
          {view === "feita" && item.completed_at && !notDone && (
            <span className={order && order.moved && level === "reordenada" ? movedStyle : styles.orderChip}>
              {order ? `${order.executed_rank}º · ` : ""}às {fmtTime(item.completed_at)}
              {order && order.executed_rank !== order.prescribed_rank ? ` · prescrito ${order.prescribed_rank}º` : ""}
            </span>
          )}
        </div>
        <div className={styles.exerciseMeta}>
          {item.duration_minutes && (
            <span className={styles.metaChip}>⏱ {item.duration_minutes} min</span>
          )}
          {sets.length > 0 && (
            <span className={styles.metaChip}>
              {sets.length}{item.sets_planned ? `/${item.sets_planned}` : ""} séries
            </span>
          )}
          {sets.length > 0 && !editing && (
            <button className={styles.editBtn} onClick={() => { setDraft(rawSets); setEditing(true); }}>
              <FontAwesomeIcon icon={faPenToSquare} />
            </button>
          )}
          {editing && (
            <>
              <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? "…" : "Salvar"}
              </button>
              <button className={styles.cancelBtn} onClick={() => setEditing(false)}>
                Cancelar
              </button>
            </>
          )}
        </div>
      </div>

      {sets.length > 0 ? (
        <div className={styles.setsTable}>
          <div className={styles.setsHeader}>
            <span>#</span><span>Reps</span><span>Peso</span><span></span>
          </div>
          {sets.map((s, i) => (
            <SetRow key={i} s={s} index={i} editing={editing} onChange={handleChange} />
          ))}
        </div>
      ) : (
        <p className={styles.noSets}>
          {notDone ? "Não realizado (fechado ao finalizar o treino)" : "Séries não registradas neste histórico"}
        </p>
      )}
    </div>
  );
}

/** Snapshot card — usado para checkins sem sessão vinculada */
function SnapshotCard({ item }) {
  return (
    <div className={styles.exerciseCard}>
      <div className={styles.exerciseName}>{item.name || "Exercício"}</div>
    </div>
  );
}

export default function SessionDetailModal({ sessionId, checkin, onClose }) {
  const isOpen = !!(sessionId || checkin);
  useBodyScrollLock(isOpen);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderView, setOrderView] = useState("prescrita"); // "prescrita" | "feita"

  const handleSaved = (itemId, newSetsLog) => {
    setSession(prev => ({
      ...prev,
      items: prev.items.map(it =>
        it.id === itemId ? { ...it, sets_log: newSetsLog } : it
      ),
    }));
  };


  useEffect(() => {
    if (!isOpen) { setSession(null); setOrderView("prescrita"); return; }
    if (!sessionId) return; // snapshot-only mode — nothing to fetch

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSession(null);
    api
      .get(`/workout-sessions/${sessionId}/detail`)
      .then(r => { if (!cancelled) setSession(r.data); })
      .catch(() => { if (!cancelled) setError("Não foi possível carregar os detalhes do treino."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [sessionId, isOpen]);

  if (!isOpen) return null;

  // Normaliza campos que vêm com nomes diferentes dependendo da origem
  // - professor view: exercise_name, is_cardio
  // - student view (UserExerciseOut): snapshot_exercise_name, exercise.name
  const exerciseNameFallback = checkin?.exercise_name || checkin?.snapshot_exercise_name || null;
  const isCardio = checkin?.is_cardio ?? (checkin?.exercise?.name === "Cardio") ?? false;

  // Decide what to display
  const title =
    (session?.plan_title ||
    checkin?.snapshot_workout_plan_title ||
    checkin?.workout_plan_title ||
    exerciseNameFallback ||
    "Detalhe do Treino").replace(/[—–]/g, "-");

  // checkin_at = professor view; date = student view (UserExerciseOut, já é datetime completo)
  const subtitle = fmtDateTime(
    session?.finished_at || checkin?.checkin_at || checkin?.date
  ) + (
    (session?.total_duration_minutes || checkin?.duration)
      ? ` • ${session?.total_duration_minutes || checkin?.duration} min`
      : ""
  );

  const totalSets = session
    ? session.items.reduce((s, it) =>
        s + (it.sets_log?.filter(s => !s.auto_filled && (s.reps != null || s.weight != null)).length || 0), 0)
    : 0;

  // Snapshot items for fallback view
  const snapshotItems = checkin?.snapshot_plan_items || [];

  // Ordem de execução (só professor/admin: o campo não vem pro aluno)
  const orderAnalysis = session?.order_analysis?.available ? session.order_analysis : null;
  const orderById = {};
  (orderAnalysis?.items || []).forEach(o => { orderById[o.id] = o; });
  const showDoneOrder = !!orderAnalysis && orderAnalysis.level !== "na_ordem" && orderView === "feita";
  const orderedItems = !session ? [] : (showDoneOrder
    ? [...session.items].sort((a, b) => String(a.completed_at || "").localeCompare(String(b.completed_at || "")))
    : session.items);

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <ModalHeader onClose={onClose} />

        {/* Header */}
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
          {session?.finished_at && <SessionSummaryButton sessionId={session.id} />}
        </div>

        {/* Summary chips */}
        <div className={styles.summaryRow}>
          {session && <span className={styles.chip}><FontAwesomeIcon icon={faDumbbell} /> {session.items.length} exercícios</span>}
          {!session && snapshotItems.length > 0 && <span className={styles.chip}><FontAwesomeIcon icon={faDumbbell} /> {snapshotItems.length} exercícios</span>}
          {totalSets > 0 && <span className={styles.chip}><FontAwesomeIcon icon={faCheck} /> {totalSets} séries</span>}
          {(session?.total_duration_minutes || checkin?.duration) && (
            <span className={styles.chip}>⏱ {session?.total_duration_minutes || checkin?.duration} min</span>
          )}
          {checkin?.points && <span className={styles.chip}><FontAwesomeIcon icon={faStar} /> +{checkin.points} pts</span>}
        </div>

        <OrderBar analysis={session?.order_analysis} view={orderView} onView={setOrderView} />

        {/* Content */}
        <div className={styles.body}>
          {loading && <div className={styles.loadingState}>Carregando...</div>}
          {error && <div className={styles.errorState}>{error}</div>}

          {/* Full session detail */}
          {!loading && session && session.items.length === 0 && (
            <p className={styles.empty}>Nenhum exercício registrado nesta sessão.</p>
          )}
          {!loading && session && orderedItems.map(item => (
            <ExerciseCard
              key={item.id}
              item={item}
              sessionId={sessionId}
              onSaved={handleSaved}
              order={orderById[item.id]}
              level={orderAnalysis?.level}
              audience={orderAnalysis?.audience}
              finishedAt={session.finished_at}
              view={orderAnalysis?.available && orderAnalysis.level !== "na_ordem" ? orderView : "prescrita"}
            />
          ))}

          {/* Cardio avulso — sem session nem snapshot_plan_items */}
          {isCardio && !sessionId && !loading && (
            <div className={styles.cardioCard}>
              <FontAwesomeIcon icon={faPersonRunning} className={styles.cardioCardIcon} />
              <div className={styles.cardioCardName}>{exerciseNameFallback || "Cardio"}</div>
              {checkin?.duration > 0 && (
                <div className={styles.cardioCardDur}>{checkin.duration} min concluídos</div>
              )}
            </div>
          )}

          {/* Snapshot fallback (sem session) */}
          {!sessionId && !loading && snapshotItems.length > 0 && (
            <>
              <div className={styles.snapshotNote}>
                <FontAwesomeIcon icon={faClipboardList} /> Checkin registrado antes do rastreamento de séries
              </div>
              {snapshotItems.map((item, i) => (
                <SnapshotCard key={i} item={item} />
              ))}
            </>
          )}

          {!sessionId && !loading && snapshotItems.length === 0 && exerciseNameFallback && (
            <div className={styles.exerciseCard}>
              <div className={styles.exerciseHeader}>
                <div className={styles.exerciseInfo}>
                  <span className={styles.exerciseName}>{exerciseNameFallback}</span>
                  {isCardio && (
                    <span className={styles.exerciseMuscle}>Cardio</span>
                  )}
                </div>
                {checkin.duration > 0 && (
                  <div className={styles.exerciseMeta}>
                    <span className={styles.metaChip}>⏱ {checkin.duration} min</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {!sessionId && !loading && snapshotItems.length === 0 && !exerciseNameFallback && (
            <p className={styles.empty}>Nenhum detalhe disponível para este checkin.</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
